import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Sparkles, Heart, Award } from "lucide-react";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import { heroSlides, homeCategoryCards, featured, about, testimonials, homeIntroImage } from "../mock/mock";

const Home = () => {
  const hero = heroSlides[0];
  return (
    <div>
      {/* HERO */}
      <section className="relative h-[82vh] min-h-[560px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img src={hero.image} alt="" className="slow-zoom w-full h-full object-cover" />
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-5 lg:px-8 flex flex-col items-center justify-center text-center">
          <h1 className="fade-up font-title italic text-lg md:text-2xl lg:text-3xl text-white max-w-2xl leading-snug" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.35)" }}>
            {hero.line1}
          </h1>
          <p className="fade-up font-script text-3xl md:text-5xl lg:text-6xl text-white mt-3" style={{ animationDelay: "120ms", textShadow: "0 2px 16px rgba(0,0,0,0.35)" }}>
            {hero.line2}
          </p>
          <Link
            to="/mes-realisations"
            className="fade-up mt-8 inline-block font-body text-[11px] md:text-xs uppercase tracking-[0.25em] text-white border-b border-white/70 hover:border-white pb-1 transition-colors duration-200"
            style={{ animationDelay: "240ms", textShadow: "0 2px 12px rgba(0,0,0,0.35)" }}
          >
            {hero.cta}
          </Link>
        </div>
      </section>

      {/* PINK MARQUEE BAR */}
      <Marquee />

      {/* INTRO / ABOUT TEASER */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <div className="aspect-[4/5] rounded-[28px] overflow-hidden shadow-[0_20px_50px_rgba(157,24,76,0.18)]">
              <img src={homeIntroImage} alt="Création pâtisserie Rim' Pâtisse" className="w-full h-full object-cover" />
            </div>
            <div className="hidden lg:flex absolute -bottom-8 -right-6 bg-white rounded-2xl shadow-xl border border-[#F3D9E2] px-7 py-5 items-center gap-3">
              <Award className="text-[#9D184C]" size={28} />
              <div>
                <div className="font-title text-2xl text-[#9D184C] leading-none">4 ans</div>
                <div className="font-body text-xs text-[#3A2A30]/60 mt-1">d'expérience</div>
              </div>
            </div>
          </div>
          <div>
            <span className="font-body text-xs tracking-[0.35em] uppercase text-[#9D184C]">Bienvenue</span>
            <h2 className="font-title text-3xl md:text-4xl lg:text-5xl text-[#3A2A30] mt-4 leading-tight">
              Chaque création raconte une histoire
            </h2>
            <div className="w-16 h-[3px] bg-[#9D184C] rounded-full my-6" />
            {about.paragraphs.slice(0, 2).map((p, i) => (
              <p key={i} className="font-body text-[#3A2A30]/70 leading-relaxed mb-4">{p}</p>
            ))}
            <Link to="/a-propos" className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-wide text-[#9D184C] hover:text-[#7A0F39] transition-colors duration-200 mt-3">
              En savoir plus sur moi <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-[#FFF6F9] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-body text-xs tracking-[0.35em] uppercase text-[#9D184C]">Nos univers</span>
            <h2 className="font-title text-3xl md:text-5xl text-[#3A2A30] mt-4">Explorez nos gourmandises</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
            {homeCategoryCards.map((cat, i) => (
              <Link
                key={cat.path}
                to={cat.path}
                className="card-hover group relative aspect-[3/4] rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(157,24,76,0.12)] fade-up"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <img src={cat.image} alt={cat.title} className="card-img w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 text-center">
                  <h3 className="font-title text-lg md:text-xl text-white">{cat.title}</h3>
                  <span className="font-body text-[11px] uppercase tracking-wide text-white/85 mt-1 inline-block">Découvrir</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <span className="font-body text-xs tracking-[0.35em] uppercase text-[#9D184C]">Coups de cœur</span>
            <h2 className="font-title text-3xl md:text-5xl text-[#3A2A30] mt-4">Nos best-sellers</h2>
          </div>
          <Link to="/gateaux" className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-wide text-[#9D184C] hover:text-[#7A0F39] transition-colors duration-200">
            Voir tout <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#9D184C] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: Heart, title: "Fait maison", text: "Des recettes artisanales, sans compromis sur la qualité." },
            { icon: Sparkles, title: "Sur mesure", text: "Chaque création est unique et pensée avec vous." },
            { icon: Award, title: "Ingrédients choisis", text: "Des produits frais et soigneusement sélectionnés." },
          ].map((v, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-white/15 flex items-center justify-center mb-4">
                <v.icon className="text-white" size={26} />
              </div>
              <h3 className="font-title text-xl text-white mb-2">{v.title}</h3>
              <p className="font-body text-sm text-white/75 leading-relaxed max-w-xs mx-auto">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#FFF6F9] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-body text-xs tracking-[0.35em] uppercase text-[#9D184C]">Témoignages</span>
            <h2 className="font-title text-3xl md:text-5xl text-[#3A2A30] mt-4">Ils nous ont fait confiance</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-[#F3D9E2] shadow-[0_10px_30px_rgba(157,24,76,0.08)] fade-up" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <Star key={s} size={18} className="fill-[#9D184C] text-[#9D184C]" />
                  ))}
                </div>
                <p className="font-body text-[#3A2A30]/75 leading-relaxed italic mb-5">« {t.text} »</p>
                <div className="font-title text-lg text-[#9D184C]">{t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-[#9D184C] overflow-hidden">
        <div className="max-w-3xl mx-auto px-5 text-center relative">
          <h2 className="font-title text-3xl md:text-5xl text-white leading-tight">Une envie, une occasion, un rêve gourmand ?</h2>
          <p className="font-body text-white/90 mt-5 text-lg">Demandez votre devis personnalisé, je donne vie à vos idées.</p>
          <Link to="/devis" className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-wide bg-white text-[#9D184C] hover:bg-[#FFF6F9] px-9 py-4 rounded-full transition-colors duration-200 mt-8">
            Demander un devis <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
